import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Space } from '@client/components/_ui/layout/Space';
import { useConfirmModalStore } from '@client/stores/confirmModalStore';
import { Modal } from 'antd';
import { CircleAlert } from 'lucide-react';
import { Fragment } from 'react';

export function ConfirmModal() {
    const { isOpen, isLoading, request, hide, setIsLoading } = useConfirmModalStore();

    if (!request) {
        return null;
    }

    // Every close or loading update names the request it belongs to: a confirmation opened from
    // inside `onOk` (a retry prompt) or right after the answer replaces this request in the slot
    // and must not be closed by this handler finishing.
    const { id, options } = request;

    function handleCancel() {
        if (isLoading) {
            return;
        }

        options.onCancel?.();
        hide(id);
    }

    return (
        <Modal
            open={isOpen}
            zIndex={2000}
            title={
                <span className="flex items-center gap-2">
                    {options.icon ?? <CircleAlert className="text-status-warning" size={20} />}
                    {options.title}
                </span>
            }
            // Lock the modal while the async action runs — no ESC, mask-click or close-button dismiss.
            keyboard={!isLoading}
            mask={{ closable: !isLoading }}
            closable={!isLoading}
            width={options.width}
            // Release hidden content after the closing animation. The request key below also resets
            // state when the next confirmation opens before that animation finishes.
            destroyOnHidden
            afterClose={options.afterClose}
            onCancel={handleCancel}
            footer={
                <Space>
                    <Button
                        type="secondary"
                        disabled={isLoading}
                        autoFocus={options.autoFocus === 'cancel'}
                        onClick={handleCancel}
                    >
                        {options.cancelText ?? 'Cancel'}
                    </Button>
                    <Button
                        type={options.okDanger ? 'danger-filled' : 'primary'}
                        loading={isLoading}
                        autoFocus={options.autoFocus === 'ok'}
                        onClick={async () => {
                            if (!options.onOk) {
                                hide(id);
                                return;
                            }

                            setIsLoading(id, true);

                            try {
                                await options.onOk();
                                hide(id);
                            } catch {
                                // Keep the modal open so the user can retry; the caller is responsible
                                // for surfacing the error (matches antd's built-in Modal.confirm behavior).
                                setIsLoading(id, false);
                            }
                        }}
                    >
                        {options.okText ?? 'OK'}
                    </Button>
                </Space>
            }
        >
            <Fragment key={id}>{options.content}</Fragment>
        </Modal>
    );
}
